import {
  CombinedSoftwarePackage,
  DeploymentDetail,
  DeploymentDetailDto,
  DeviceDetail,
  DeviceDetailDto,
  FieldDeviceConfigurationComponent,
  FieldDeviceIdentifier,
  FieldDeviceIdentifierDto,
  FieldDeviceProperty,
  FieldDevicePropertyDto,
  FieldDeviceReportedConfig,
  FieldDeviceSetting,
  FieldDeviceSettingDto,
  IdentifierTypes,
  InternalIdentifierTypes,
  PropertyTypes,
  Selector,
  SelectorDto,
  SoftwarePackageServiceCodes
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  toChangeLog,
  toChangeLogDto,
  toDateTime
} from "@libs/common/common.utils";

export const toFieldDeviceIdentifier = (
  identifier: FieldDeviceIdentifierDto
) => {
  return {
    ...identifier,
    lastObservedUtc: toDateTime(identifier.lastObservedUtc),
    prevObservedUtc: toDateTime(identifier.prevObservedUtc),
    firstObservedUtc: toDateTime(identifier.firstObservedUtc)
  } as FieldDeviceIdentifier;
};

export const toFieldDeviceProperty = (property: FieldDevicePropertyDto) => {
  return {
    ...property,
    lastObservedUtc: toDateTime(property.lastObservedUtc)
  } as FieldDeviceProperty;
};

export const toDeviceDetail = (dto: DeviceDetailDto): DeviceDetail => {
  return {
    ...dto,
    properties: dto.properties.map(toFieldDeviceProperty),
    identifiers: dto.identifiers.map(toFieldDeviceIdentifier)
  };
};

export const toSelector = (dto: SelectorDto): Selector => {
  return {
    ...dto,
    rules: dto.rules.map(x => {
      const { changeLog, ...rest } = x;
      return {
        ...rest,
        changeLog: toChangeLog(x.changeLog)
      };
    }),
    changeLog: toChangeLog(dto.changeLog)
  };
};

export const toSelectorDto = (dto: Selector): SelectorDto => {
  return {
    ...dto,
    rules: dto.rules.map(x => {
      const { changeLog, ...rest } = x;
      return {
        ...rest,
        changeLog: toChangeLogDto(x.changeLog)
      };
    }),
    changeLog: toChangeLogDto(dto.changeLog)
  };
};

export const toFieldDeviceSetting = (
  dto: FieldDeviceSettingDto
): FieldDeviceSetting => {
  return {
    ...dto,
    changeLog: toChangeLog(dto.changeLog)
  };
};

export const toFieldDeviceSettingDto = (
  dto: FieldDeviceSetting
): FieldDeviceSettingDto => {
  return {
    ...dto,
    changeLog: toChangeLogDto(dto.changeLog)
  };
};

export const shorthandServiceName = (
  softwarePackage: CombinedSoftwarePackage
) => {
  switch (softwarePackage.softwarePackageCode) {
    case SoftwarePackageServiceCodes.FieldController:
      return "Controller";
    case SoftwarePackageServiceCodes.RecoveryAgent:
      return "Recovery Agent";
    default:
      return softwarePackage.name;
  }
};

export const combinedSoftwarePackages = (
  desiredSoftwarePackage: FieldDeviceConfigurationComponent[],
  reportedSoftwarePackage: FieldDeviceReportedConfig[]
) => {
  const combined = [] as CombinedSoftwarePackage[];

  // Look over desired modules and push
  desiredSoftwarePackage.forEach(desired => {
    combined.push({
      desiredVersion: desired.version,
      currentVersion: reportedSoftwarePackage.find(
        sp => sp.softwarePackageCode === desired.code
      )?.softwarePackageVersion,
      id: desired.id,
      softwarePackageCode: desired.code,
      softwarePackageType: desired.type!,
      name: desired.name
    });
  });

  // Look over reported modules and push if not already added.
  reportedSoftwarePackage.forEach(reported => {
    !combined.some(
      sp => sp.softwarePackageCode === reported.softwarePackageCode
    ) &&
      combined.push({
        desiredVersion: desiredSoftwarePackage.find(
          sp => sp.code === reported.softwarePackageCode
        )?.version,
        currentVersion: reported.softwarePackageVersion,
        id: reported.softwarePackageId,
        softwarePackageCode: reported.softwarePackageCode,
        softwarePackageType: reported.softwarePackageType,
        name: reported.softwarePackageDisplayName
      });
  });

  return combined;
};

export const toDeploymentDetail = (
  dto: DeploymentDetailDto
): DeploymentDetail => {
  return {
    ...dto,
    deploymentRings: dto.deploymentRings.map(deploymentRing => {
      return {
        ...deploymentRing,
        rollouts:
          deploymentRing.rollouts?.map(rollout => {
            return {
              ...rollout,
              initialDownloadStartUtc: toDateTime(
                rollout.initialDownloadStartUtc
              ),
              initialDownloadEndUtc: toDateTime(rollout.initialDownloadEndUtc),
              initialInstallStartUtc: toDateTime(
                rollout.initialInstallStartUtc
              ),
              initialInstallEndUtc: toDateTime(rollout.initialInstallEndUtc)
            };
          }) ?? []
      };
    })
  };
};

export function getPropertyAndIdentifierOptions() {
  const internalIdentifierTypeValues = Object.keys(InternalIdentifierTypes).map(
    key => InternalIdentifierTypes[key]
  );

  const identifierTypeValues = Object.keys(IdentifierTypes).map(
    key => IdentifierTypes[key]
  );

  const propertyTypeValues = Object.keys(PropertyTypes).map(
    key => PropertyTypes[key]
  );

  return (
    internalIdentifierTypeValues.concat(
      identifierTypeValues.concat(propertyTypeValues)
    ) ?? []
  ).map(attributeType => ({
    key: attributeType,
    text: attributeType
  }));
}
